import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  DocumentTextIcon,
  KeyIcon,
  ChartBarIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CogIcon,
  SparklesIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';
import UserManagement from '../components/admin/UserManagement';
import LicenseManagement from '../components/admin/LicenseManagement';
import SubscriptionManagement from '../components/admin/SubscriptionManagement';
import PlanManagement from '../components/admin/PlanManagement';
import UpgradeRequests from '../components/admin/UpgradeRequests';
import LLMSettings from '../components/admin/LLMSettings';
import HumanSupport from '../components/admin/HumanSupport';
import TrainingDataManagement from '../components/admin/TrainingDataManagement';
import ScenarioManagement from '../components/admin/ScenarioManagement';
import SCOSettings from '../components/admin/SCOSettings';
import HSCodeManagement from '../components/admin/HSCodeManagement';
import AdminBreadcrumb from '../components/admin/AdminBreadcrumb';
import SROManagement from '../components/admin/SROManagement';
import SubscriptionHistoryAdmin from '../components/admin/SubscriptionHistoryAdmin';
import AdminLLMDashboard from '../components/admin/AdminLLMDashboard';
import AdminUserQuotaManager from '../components/admin/AdminUserQuotaManager';
import AdminTokenUsageAnalytics from '../components/admin/AdminTokenUsageAnalytics';
import AdminAdjustmentHistory from '../components/admin/AdminAdjustmentHistory';
import PagePermissionsManager from '../components/admin/PagePermissionsManager';
import MasterInviteCodeManager from '../components/admin/MasterInviteCodeManager';
import BlogManagement from './admin/BlogManagement';

const AdminDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({
    management: true,
    system: false,
    development: false
  });

  // Get active tab from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadAdminStats();
    }
  }, [user]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  // Update active tab when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabFromUrl = urlParams.get('tab') || 'overview';
    setActiveTab(tabFromUrl);
  }, [location.search]);

  // Handle tab change with URL update
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false); // Close sidebar on mobile after selection
    if (tabId === 'overview') {
      navigate('/app/admin');
    } else {
      navigate(`/app/admin?tab=${tabId}`);
    }
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Menu structure with categories
  const menuCategories = [
    {
      id: 'overview',
      name: 'Dashboard Overview',
      icon: ChartBarIcon,
      items: [
        { id: 'overview', name: 'System Overview', icon: ChartBarIcon }
      ]
    },
    {
      id: 'management',
      name: 'User & License Management',
      icon: UsersIcon,
      items: [
        { id: 'users', name: 'User Management', icon: UsersIcon },
        { id: 'licenses', name: 'License Management', icon: KeyIcon },
        { id: 'subscriptions', name: 'Subscriptions', icon: CreditCardIcon },
        { id: 'plans', name: 'Plan Management', icon: CreditCardIcon },
        { id: 'upgrade-requests', name: 'Upgrade Requests', icon: ClockIcon },
        { id: 'subscription-history', name: 'Subscription History', icon: CreditCardIcon },
        { id: 'master-invites', name: 'Master User Invitations', icon: KeyIcon },
        { id: 'hscode-management', name: 'HS Code Management', icon: DocumentTextIcon },
        { id: 'sro-management', name: 'SRO Knowledge Base', icon: DocumentTextIcon },
        { id: 'page-permissions', name: 'Page Permissions', icon: KeyIcon }
      ]
    },
    {
      id: 'llm-management',
      name: 'LLM Token Management',
      icon: SparklesIcon,
      items: [
        { id: 'llm-dashboard', name: 'Token Dashboard', icon: ChartBarIcon },
        { id: 'quota-manager', name: 'Quota Manager', icon: UsersIcon },
        { id: 'usage-analytics', name: 'Usage Analytics', icon: ChartBarIcon },
        { id: 'adjustment-history', name: 'Adjustment History', icon: DocumentTextIcon }
      ]
    },
    {
      id: 'blog-management',
      name: 'Blog Management',
      icon: PencilIcon,
      items: [
        { id: 'blog', name: 'Blog Posts', icon: PencilIcon }
      ]
    },
    {
      id: 'system',
      name: 'System & Support',
      icon: CpuChipIcon,
      items: [
        { id: 'scenarios', name: 'Scenario Management', icon: DocumentTextIcon },
        { id: 'human-support', name: 'Human Support', icon: ChatBubbleLeftRightIcon },
        { id: 'training-data', name: 'Training Data', icon: BookOpenIcon },
        { id: 'llm-settings', name: 'LLM Settings', icon: CpuChipIcon },
        { id: 'sco-settings', name: 'SCO Settings', icon: CogIcon }
      ]
    }
  ];

  const loadAdminStats = async () => {
    try {
      setLoading(true);
      const [statsResponse, usersResponse] = await Promise.all([
        apiClient.get('/admin/dashboard/stats'),
        apiClient.get('/admin/llm/users?limit=1000')
      ]);
      setStats(statsResponse.data.data);
      setUsers(usersResponse.data.data?.users || []);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access the admin dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, description }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="text-lg font-medium text-gray-900">{value}</dd>
              {description && (
                <dd className="text-xs text-gray-400 mt-1">{description}</dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Admin Panel</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* System Status */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">System Status</span>
            <div className="flex items-center">
              <div className="h-2 w-2 bg-green-400 rounded-full mr-2"></div>
              <span className="text-xs text-gray-600">Online</span>
            </div>
          </div>
          {stats && (
            <div className="mt-2 text-xs text-gray-500">
              <div>Users: {stats.users?.total_users || 0}</div>
              <div>API Calls (24h): {stats.api?.calls_24_hours || 0}</div>
            </div>
          )}
        </div>

        {/* Sidebar navigation */}
        <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
          {menuCategories.map((category) => (
            <div key={category.id}>
              {category.id === 'overview' ? (
                // Overview is a single item, not a category
                <button
                  onClick={() => handleTabChange('overview')}
                  className={`${
                    activeTab === 'overview'
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group w-full flex items-center pl-2 pr-2 py-2 border-l-4 text-sm font-medium rounded-r-md`}
                >
                  <category.icon className="mr-3 h-5 w-5" />
                  {category.name}
                </button>
              ) : (
                // Category with expandable items
                <div>
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="group w-full flex items-center justify-between pl-2 pr-2 py-2 text-left text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md"
                  >
                    <div className="flex items-center">
                      <category.icon className="mr-3 h-5 w-5" />
                      {category.name}
                    </div>
                    {expandedCategories[category.id] ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  
                  {expandedCategories[category.id] && (
                    <div className="mt-1 space-y-1">
                      {category.items.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleTabChange(item.id)}
                          className={`${
                            activeTab === item.id
                              ? 'bg-blue-50 border-blue-500 text-blue-700'
                              : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          } group w-full flex items-center pl-8 pr-2 py-2 border-l-4 text-sm font-medium rounded-r-md`}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-3 py-3">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="ml-4 lg:ml-0">
                <h1 className="text-xl font-bold text-gray-900">
                  {menuCategories
                    .flatMap(cat => cat.items || [cat])
                    .find(item => item.id === activeTab)?.name || 'Admin Dashboard'}
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  System administration and user management
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-2">
          <AdminBreadcrumb activeTab={activeTab} menuCategories={menuCategories} />
          
          <div className="bg-white shadow rounded-lg">
            {activeTab === 'overview' && (
              <div className="p-4 space-y-4">
                {/* System Statistics */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">System Overview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Total Users"
                    value={stats?.users?.total_users || 0}
                    icon={UsersIcon}
                    color="text-blue-500"
                    description={`${stats?.users?.new_users_30_days || 0} new this month`}
                  />
                  <StatCard
                    title="Active Licenses"
                    value={stats?.subscriptions?.active_licenses || 0}
                    icon={KeyIcon}
                    color="text-green-500"
                    description={`${stats?.subscriptions?.total_subscriptions || 0} total subscriptions`}
                  />
                  <StatCard
                    title="Total Invoices"
                    value={stats?.invoices?.total_invoices || 0}
                    icon={DocumentTextIcon}
                    color="text-purple-500"
                    description={`${stats?.invoices?.new_invoices_30_days || 0} new this month`}
                  />
                  <StatCard
                    title="API Calls (24h)"
                    value={stats?.api?.calls_24_hours || 0}
                    icon={ChartBarIcon}
                    color="text-orange-500"
                    description={`${Math.round(stats?.api?.avg_response_time || 0)}ms avg response`}
                  />
                </div>
              </div>

              {/* HS Code Statistics */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">HS Code Management</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard
                    title="Total HS Codes"
                    value={stats?.hsCodes?.total_hs_codes || 0}
                    icon={DocumentTextIcon}
                    color="text-blue-500"
                    description="In database"
                  />
                  <StatCard
                    title="Active HS Codes"
                    value={stats?.hsCodes?.active_hs_codes || 0}
                    icon={CheckCircleIcon}
                    color="text-green-500"
                    description="Valid and active"
                  />
                  <StatCard
                    title="Inactive HS Codes"
                    value={stats?.hsCodes?.inactive_hs_codes || 0}
                    icon={ExclamationTriangleIcon}
                    color="text-red-500"
                    description="Deactivated codes"
                  />
                </div>
                {stats?.hsCodes?.last_updated && (
                  <div className="mt-3 text-xs text-gray-500">
                    Last updated: {new Date(stats.hsCodes.last_updated).toLocaleString()}
                  </div>
                )}
              </div>

              {/* Invoice Status Breakdown */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard
                    title="Submitted"
                    value={stats?.invoices?.submitted_invoices || 0}
                    icon={CheckCircleIcon}
                    color="text-green-500"
                  />
                  <StatCard
                    title="Pending"
                    value={stats?.invoices?.pending_invoices || 0}
                    icon={ClockIcon}
                    color="text-yellow-500"
                  />
                  <StatCard
                    title="Errors"
                    value={stats?.invoices?.error_invoices || 0}
                    icon={ExclamationTriangleIcon}
                    color="text-red-500"
                  />
                </div>
              </div>

              {/* User Role Breakdown */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">User Roles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <StatCard
                    title="Regular Users"
                    value={stats?.users?.regular_users || 0}
                    icon={UsersIcon}
                    color="text-blue-500"
                  />
                  <StatCard
                    title="Administrators"
                    value={stats?.users?.admin_users || 0}
                    icon={UsersIcon}
                    color="text-red-500"
                  />
                </div>
              </div>

              {/* API Performance */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">API Performance</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard
                    title="Total API Calls"
                    value={stats?.api?.total_api_calls || 0}
                    icon={ChartBarIcon}
                    color="text-blue-500"
                  />
                  <StatCard
                    title="Successful Calls"
                    value={stats?.api?.successful_calls || 0}
                    icon={CheckCircleIcon}
                    color="text-green-500"
                  />
                  <StatCard
                    title="Failed Calls"
                    value={stats?.api?.failed_calls || 0}
                    icon={ExclamationTriangleIcon}
                    color="text-red-500"
                  />
                </div>
              </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="p-6">
                <UserManagement />
              </div>
            )}

            {activeTab === 'licenses' && (
              <div className="p-6">
                <LicenseManagement />
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div className="p-6">
                <SubscriptionManagement />
              </div>
            )}

            {activeTab === 'plans' && (
              <div className="p-6">
                <PlanManagement />
              </div>
            )}

            {activeTab === 'upgrade-requests' && (
              <div className="p-6">
                <UpgradeRequests />
              </div>
            )}

            {activeTab === 'hscode-management' && (
              <div className="p-6">
                <HSCodeManagement />
              </div>
            )}

            {activeTab === 'sro-management' && (
              <div className="p-6">
                <SROManagement />
              </div>
            )}

            {activeTab === 'subscription-history' && (
              <div className="p-6">
                <SubscriptionHistoryAdmin />
              </div>
            )}

            {activeTab === 'page-permissions' && (
              <div className="p-6">
                <PagePermissionsManager />
              </div>
            )}

            {activeTab === 'master-invites' && (
              <div className="p-6">
                <MasterInviteCodeManager />
              </div>
            )}

            {activeTab === 'llm-dashboard' && (
              <div className="p-6">
                <AdminLLMDashboard />
              </div>
            )}

            {activeTab === 'quota-manager' && (
              <div className="p-6">
                <AdminUserQuotaManager users={users} onAdjustmentMade={loadAdminStats} />
              </div>
            )}

            {activeTab === 'usage-analytics' && (
              <div className="p-6">
                <AdminTokenUsageAnalytics />
              </div>
            )}

            {activeTab === 'adjustment-history' && (
              <div className="p-6">
                <AdminAdjustmentHistory />
              </div>
            )}

            {activeTab === 'scenarios' && (
              <div className="p-6">
                <ScenarioManagement />
              </div>
            )}

            {activeTab === 'human-support' && (
              <div className="p-6">
                <HumanSupport />
              </div>
            )}

            {activeTab === 'training-data' && (
              <div className="p-6">
                <TrainingDataManagement />
              </div>
            )}

            {activeTab === 'llm-settings' && (
              <div className="p-6">
                <LLMSettings />
              </div>
            )}

            {activeTab === 'sco-settings' && (
              <div className="p-4">
                <SCOSettings />
              </div>
            )}


            {activeTab === 'blog' && (
              <div className="p-6">
                <BlogManagement />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
